import React from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import ImageView from '../../components/ImageView';
import { useLang } from '../../context/LangContext';

const LotHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  padding: 0 0 80px 0; /* 하단 여백으로 바텀 네비게이션과의 간격 확보 */
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
`;

// 웹용 제목 컴포넌트 (FormTitle과 동일한 스타일)
const WebTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #000;
  text-align: left;
  margin: 0 0 20px 0;
  grid-column: 1 / -1; /* 그리드 전체 너비를 차지 */
  
  @media (max-width: 767px) {
    display: none; /* 모바일에서는 숨김 */
  }
`;

// LOT 카드 컨테이너 (임시로 경매 카드와 동일한 스타일 사용)
const LotCard = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

// 이미지 컨테이너
const ImageContainer = styled.div`
  width: 120px;
  height: 120px;
  align-self: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

// LOT 정보 컨테이너
const LotInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// LOT 제목
const LotTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #69b5b0;
  margin: 0;
  line-height: 1.3;
  text-align: left;
  align-self: flex-start;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// LOT 정보
const LotInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #666;
  line-height: 1.4;
  width: 100%;
`;

const LotLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #666;
`;

const LotValue = styled.span`
  font-size: 12px;
  color: #666;
`;

// 다국어 텍스트 객체
const lotHistoryTexts = {
  ko: {
    title: 'LOT 내역',
    lotNumber: 'LOT 번호',
    artist: '작가',
    estimatePrice: '추정가',
    auctionDate: '경매일'
  },
  en: {
    title: 'LOT History',
    lotNumber: 'LOT Number',
    artist: 'Artist',
    estimatePrice: 'Estimate',
    auctionDate: 'Auction Date'
  }
};

// 더미 데이터
const dummyLots = [
  { 
    id: 1, 
    title: '무제',
    artist: '김환기',
    lotNumber: 'LOT 001',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '5,000만원 ~ 8,000만원',
    auctionDate: '25.09.05'
  },
  { 
    id: 2, 
    title: '산수도',
    artist: '이중섭',
    lotNumber: 'LOT 002',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '3,000만원 ~ 5,000만원',
    auctionDate: '25.09.05'
  },
  { 
    id: 3, 
    title: '풍경',
    artist: '박수근',
    lotNumber: 'LOT 003',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '2,000만원 ~ 3,000만원',
    auctionDate: '25.09.06'
  },
  { 
    id: 4, 
    title: '정물',
    artist: '장욱진',
    lotNumber: 'LOT 004',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '1,500만원 ~ 2,500만원',
    auctionDate: '25.09.06'
  },
  { 
    id: 5, 
    title: '추상',
    artist: '유영국',
    lotNumber: 'LOT 005',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '4,000만원 ~ 6,000만원',
    auctionDate: '25.09.07'
  },
  { 
    id: 6, 
    title: '인물화',
    artist: '김종영',
    lotNumber: 'LOT 006',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '1,000만원 ~ 1,500만원',
    auctionDate: '25.09.07'
  },
  { 
    id: 7, 
    title: '도시풍경',
    artist: '이응노',
    lotNumber: 'LOT 007',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '3,500만원 ~ 5,500만원',
    auctionDate: '25.09.08'
  },
  { 
    id: 8, 
    title: '꽃',
    artist: '천경자',
    lotNumber: 'LOT 008',
    imageUrl: '/common/nodata.svg',
    estimatePrice: '2,500만원 ~ 4,000만원',
    auctionDate: '25.09.08'
  }
];

const LotHistory: React.FC = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const texts = lotHistoryTexts[lang];

  const handleCardClick = (lotId: number) => {
    devLog('LOT 카드 클릭:', lotId);
    navigate(`/lot-detail/${lotId}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <MainLayout>
      <CenteredLayout>
        <ProjectAppbar 
          title={texts.title}
          showBackButton={true}
          onBack={handleBack}
        />
        <LotHistoryContainer>
          <ContentContainer>
            <WebTitle>{texts.title}</WebTitle>
            {dummyLots.map((lot) => (
                <LotCard key={lot.id} onClick={() => handleCardClick(lot.id)}>
                  <ImageContainer>
                    <ImageView
                      imageUrl={lot.imageUrl}
                      width={120}
                      height={120}
                      borderRadius={8}
                      alt={lot.title}
                    />
                  </ImageContainer>
                  
                  <LotInfoContainer>
                    <LotInfo>
                      <LotLabel>{texts.lotNumber}</LotLabel>
                      <LotValue>{lot.lotNumber}</LotValue>
                    </LotInfo>
                    
                    <LotTitle>{lot.title}</LotTitle>
                    
                    <LotInfo>
                      <LotLabel>{texts.artist}</LotLabel>
                      <LotValue>{lot.artist}</LotValue>
                    </LotInfo>
                    
                    <LotInfo>
                      <LotLabel>{texts.estimatePrice}</LotLabel>
                      <LotValue>{lot.estimatePrice}</LotValue>
                    </LotInfo>
                    
                    <LotInfo>
                      <LotLabel>{texts.auctionDate}</LotLabel>
                      <LotValue>{lot.auctionDate}</LotValue>
                    </LotInfo>
                  </LotInfoContainer>
                </LotCard>
              ))}
            </ContentContainer>
          </LotHistoryContainer>
        </CenteredLayout>
      </MainLayout>
    );
};

export default LotHistory;
